import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Box, Avatar, Stack, Popover, List, ListItem, ListItemText,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useSelector } from "react-redux";
import useDeepEqualSelector from '../../../../../Hooks/useDeepEqualSelector';
import {getStatusColor} from "../../../../../Setup";
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSame from 'dayjs/plugin/isSameOrAfter';
import { RosterModal } from '../Modals/RosterModal';
import {setModal} from "../../../../Common/App";
import {cleanName, getRosterEditBlockReason} from "../../../../../Setup/Config/formats";
import { usePermissions } from '../../../../../Hooks/usePermissions';
import {Toastr} from "../../../../../Components";
dayjs.extend(isSame);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// === Simple & Sleek Styled Components ===
const StyledPopoverBox = styled(Box)(({ theme, status }) => {
    // Get status color from theme
    const getStatusThemeColor = (status) => {
        const statusColorMap = {
            'ON_CALL': theme.palette.info,
            'OFF': theme.palette.error,
            '24_HOURS': theme.palette.warning,
            'ON_DUTY': theme.palette.success,
            'ON_SITE': theme.palette.success,
        };
        return statusColorMap[status.name] || theme.palette.primary;
    };

    const statusColor = getStatusThemeColor(status);

    return {
        padding: theme.spacing(2),
        fontSize: '0.875rem',
        backgroundColor: statusColor.main,
        color: statusColor.contrastText || '#ffffff',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        borderRadius: '8px',
        position: 'relative',
        minWidth: '280px',
        maxWidth: '320px',
        border: `1px solid ${statusColor.dark}`,
        animation: 'fadeIn 0.2s ease-out',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            right: '100%',
            transform: 'translateY(-50%)',
            border: 'solid transparent',
            borderRightColor: statusColor.main,
            borderWidth: '8px',
            pointerEvents: 'none',
        },
        '& .popover-header': {
            textAlign: 'center',
            padding: theme.spacing(1.5, 0),
            marginBottom: theme.spacing(1.5),
            borderBottom: `1px solid ${statusColor.dark}`,
            '& .employee-name': {
                color: statusColor.contrastText || '#ffffff',
                fontWeight: 600,
                fontSize: '1rem',
                marginBottom: theme.spacing(0.5),
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            },
            '& .date-display': {
                color: statusColor.contrastText || '#ffffff',
                fontSize: '0.8rem',
                fontWeight: 400,
                opacity: 0.8,
            },
        },
        '& .MuiList-root': {
            padding: 0,
            '& .MuiListItem-root': {
                padding: theme.spacing(1, 0),
                '&:last-child': {
                    paddingBottom: 0,
                },
                '& .MuiListItemIcon-root': {
                    display: 'none',
                },
                '& .MuiListItemText-root': {
                    margin: 0,
                    '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        color: statusColor.contrastText || '#ffffff',
                        fontSize: '0.8rem',
                        opacity: 0.9,
                    },
                    '& .MuiListItemText-secondary': {
                        fontWeight: 500,
                        color: statusColor.contrastText || '#ffffff',
                        fontSize: '0.85rem',
                        padding: theme.spacing(0.5, 1),
                        borderRadius: '4px',
                        minWidth: '80px',
                        textAlign: 'center',
                        display: 'inline-block',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                },
            },
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0,
                transform: 'translateY(8px)',
            },
            '100%': {
                opacity: 1,
                transform: 'translateY(0)',
            },
        },
    };
});

const CalendarBox = styled(Box)(({ theme }) => ({
    borderTop: '1px solid #efefef',
    '& .MuiTable-root': {
        borderCollapse: 'collapse',
        '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
        },
        '& thead th': {
            borderTop: 'none',
        },
        '& tbody tr:last-of-type td': {
            borderBottom: 'none',
        },
        '& th:first-of-type, & td:first-of-type': {
            borderLeft: 'none',
        },
        '& th:last-of-type, & td:last-of-type': {
            borderRight: 'none',
        },

        '& tbody tr td': {
            minHeight: '40px',
            height: '40px',
        },
        '& .employee-details-label': {
            textTransform: 'capitalize',
            fontSize: '1rem',
            color: theme.palette.text.primary,
            fontWeight: 400,
            margin: 0,
        },
        '& thead th:not(:first-of-type)': {
            padding: '3px!important',
            backgroundColor: '#fff',
            '& .calendar-month': {
                fontSize: '0.70rem',
                textTransform: 'capitalize',
            },
            '& .calendar-date': {
                fontSize: '0.9rem',
                fontWeight: 600,
                color: theme.palette.text.secondary,
            },
            '& .calendar-day': {
                fontSize: '0.70rem',
                textTransform: 'capitalize',
            },
        },
        '& tbody tr td:first-of-type': {
            padding: '8px 8px!important',
            '& .MuiBox-root': {
                marginLeft: '8px',
            },
            '& .employee-name': {
                fontSize: '0.85rem',
                marginTop: '3px',
                marginBottom: '0px',
                textDecoration: 'none',
                color: theme.palette.text.primary,
            },
            '& .employee-code': {
                fontSize: '0.70rem',
                color: theme.palette.text.secondary,
            },
            '& .leave-balance': {
                fontSize: '0.75rem',
                border: `1px solid #efefef`,
                background: theme.palette.secondary.light,
                padding: '2px 2px',
                width: '40px',
                textAlign: 'center',
            }
        },
        '& tbody tr td:not(:first-of-type)': {
            padding: '4px !important',
            '& .MuiBox-root': {
                padding: '4px!important',
                width: '100%',
                height: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }
        }
    }
}));

const StickyTableCell = styled(TableCell)(({ theme }) => ({
    position: 'sticky',
    left: 0,
    zIndex: 5,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `2px 0 0 ${theme.palette.divider}`,
    '&.MuiTableCell-stickyHeader': {
        zIndex: 101,
    }
}));

export const EmployeeCalendar = () => {

    const dispatch = useDispatch();
    const theme = useTheme();
    const {hasPermission} = usePermissions();
    const itemsRaw = useSelector(state => state.rosters.calendar.list);
    const items = useMemo(() => itemsRaw || [], [itemsRaw]);
    const config = useSelector(state => state.rosters.calendar.config) || {};
    const rosters = useSelector(state => state.rosters?.list || []);
    const rosterSettings = useSelector(state => state.app.settings || {});
    const holidays = useDeepEqualSelector(state => state.app.config.holidays || []);
    
    const [anchorEl, setAnchorEl] = useState(null);
    const [popoverContent, setPopoverContent] = useState(null);
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('');
    const selectedMonth = config.filters?.month ? dayjs(config.filters.month) : dayjs();
    const [, setContainerWidth] = useState(0);
    const containerRef = useRef(null);
    const employeeColWidth = 220;
    const cellWidth = 50; // Fixed width for square cells

    // Clear message after showing
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage('');
                setErrors({});
                setStatus('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    // Remove duplicates using useMemo
    const uniqueEmployees = useMemo(() => {
        if (!items) return [];
        const uniqueIds = new Set();
        return items.filter(employee => {
            if (uniqueIds.has(employee.id)) {
                return false;
            } else {
                uniqueIds.add(employee.id);
                return true;
            }
        });
    }, [items]);

    useEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width - 16);
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const headerDays = useMemo(() => {
        const days = [];
        const daysInMonth = selectedMonth.daysInMonth();
        for (let i = 1; i <= daysInMonth; i++) {
            const date = selectedMonth.date(i);
            days.push({ day: i, weekday: date.format('ddd'), dayjsObject: date });
        }
        return days;
    }, [selectedMonth]);

    const holidayDates = useMemo(() => {
        return new Set(holidays.map(holiday => dayjs(holiday.holiday_date).format('YYYY-MM-DD')));
    }, [holidays]);

    // Updated status codes for roster management
    const statusCodes = {
        'ON_CALL': 'OC',      // On Call
        'ON_SITE': 'OS',      // On Site
        'OFF': 'O',          // Off
        '24_HOURS': '24',    // 24 Hours
        'ON_DUTY': 'OD',    // Time Range
        'HOLIDAY': 'H',      // Holiday
        'WEEKEND': 'W',      // Weekend
    };

    const statusColorMap = useMemo(() => ({
        'ON_CALL': 'info',
        'OFF': 'error',
        '24_HOURS': 'warning',
        'ON_DUTY': 'primary',
        'HOLIDAY': 'accent',
        'WEEKEND': 'warning',
        'ON_SITE': 'success',
    }), []);

    const getCellColor = useCallback((status, level, effect) => {
        const colorName = statusColorMap[status];
        return getStatusColor(theme, colorName, level, effect);
    }, [theme, statusColorMap]);

    // Handle cell click with date restrictions
    const handleCellClick = (item, dateKey) => {
        const rosterData = item.rosters?.[dateKey];
        const blockReason = getRosterEditBlockReason({
            clickedDate: dateKey,
            settings: rosterSettings,
            rosters,
            rosterEntry: rosterData,
            canEditLocked: hasPermission('roster.roster.edit-locked-entries'),
        });

        if (blockReason) {
            setErrors({ roster_date: blockReason });
            setMessage(blockReason);
            setStatus('error');
            return;
        }

        dispatch(setModal({component: <RosterModal item={item} date={dateKey} />, open: true}))
    };

    const today = dayjs().startOf('day');

    const handlePopoverOpen = (event, item, dateKey, status) => {
        let details = {};
        const rosterData = item.rosters?.[dateKey];

        if (rosterData) {
            details.status = rosterData.status;
            details.start_time = rosterData.start_time;
            details.end_time = rosterData.end_time;
            details.notes = rosterData.notes;
            details.is_locked = rosterData.is_locked;
            details.event_name = rosterData.event_name;
            details.venue_name = rosterData.venue_name;
            details.location_name = rosterData.location_name;
        }

        setPopoverContent({
            employeeName: item.first_name,
            date: dayjs(dateKey).format('DD-MM-YYYY'),
            status: statusCodes[status] || status,
            details: details,
        });
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <CalendarBox ref={containerRef} sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', position: 'relative' }}>
                    <Table stickyHeader sx={{ minWidth: employeeColWidth + (headerDays.length * cellWidth) }}>
                        <TableHead>
                            <TableRow>
                                <StickyTableCell sx={{ minWidth: employeeColWidth }}>
                                    <Typography className="employee-details-label">
                                        Employee Details - {config.total}
                                    </Typography>
                                </StickyTableCell>
                                {headerDays.map((date, index) => {
                                    const dateKey = date.dayjsObject.format('YYYY-MM-DD');
                                    const isHolidayDate = holidayDates.has(dateKey);
                                    const isToday = date.dayjsObject.isSame(today, 'day');
                                    const isWeekend = date.weekday === 'Sat' || date.weekday === 'Sun';
                                    // Determine header status for color
                                    const headerStatus = isHolidayDate ? 'HOLIDAY' : isWeekend ? 'WEEKEND' : null;

                                    return (
                                        <TableCell
                                            key={index}
                                            align="center"
                                            sx={{
                                                width: cellWidth,
                                                minWidth: cellWidth,
                                                backgroundColor: headerStatus ? `${getCellColor(headerStatus, '0.4', 'lighten')}!important` : '#ffffff!important',
                                                border: `1px solid ${theme.palette.divider}!important`,
                                                ...(isToday && {
                                                    '@keyframes rosterTodayColPulse': {
                                                        '0%, 100%': {
                                                            boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.secondary.main, 0.95)}`,
                                                        },
                                                        '50%': {
                                                            boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.secondary.main, 0.22)}`,
                                                        },
                                                    },
                                                    animation: 'rosterTodayColPulse 2.4s ease-in-out infinite',
                                                    zIndex: 1,
                                                    '@media (prefers-reduced-motion: reduce)': {
                                                        animation: 'none',
                                                        boxShadow: `inset 0 0 0 2px ${theme.palette.secondary.main}`,
                                                    },
                                                }),
                                            }}
                                        >
                                            <Stack>
                                                <Typography className={headerStatus ? "calendar-month text-dark" : "calendar-month"}>{selectedMonth.format('MMM')}</Typography>
                                                <Typography className="calendar-date">{String(date.day).padStart(2, '0')}</Typography>
                                                <Typography className={headerStatus ? "calendar-day text-dark" : "calendar-day"}>{date.weekday}</Typography>
                                            </Stack>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {uniqueEmployees.map((item) => (
                                <TableRow key={item.id}>
                                    <StickyTableCell sx={{ minWidth: employeeColWidth }}>
                                        <Stack direction="row" justifyContent={"space-between"} spacing={2} alignItems="center">
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar 
                                                    src={item?.avatar_url || '/images/user-icon-3.jpg'} 
                                                    sx={{ width: 30, height: 30 }}
                                                />
                                                <Box>
                                                    <Typography className="employee-name">
                                                        {item?.first_name || ''} {item?.last_name || ''}
                                                    </Typography>
                                                    <Typography className="employee-code">
                                                        {item?.department}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </StickyTableCell>
                                    {headerDays.map((date, index) => {
                                        const dateKey = selectedMonth.date(date.day).format('YYYY-MM-DD');
                                        let status = null;

                                        // Check for roster data first
                                        const rosterData = item.rosters?.[dateKey];
                                        
                                        if (rosterData) {
                                            // Handle both string and object status formats
                                            if (typeof rosterData.status === 'string') {
                                                status = rosterData.status;
                                            } else if (rosterData.status?.name) {
                                                status = rosterData.status.name;
                                            } else {
                                                status = null;
                                            }
                                        }
                                        // Only set status for display purposes if no roster data
                                        // Do NOT set status for color purposes (keep status = null for white background)

                                        const handleMouseEnter = (event) => {
                                            if (rosterData) {
                                                handlePopoverOpen(event, item, dateKey, status);
                                            }
                                        };

                                        return (
                                            <TableCell key={`${item.user_code}-${index}`} align="center" sx={{ width: cellWidth, minWidth: cellWidth }}>
                                                <Box
                                                    aria-owns={open ? 'mouse-over-popover' : undefined}
                                                    aria-haspopup="true"
                                                    onMouseEnter={handleMouseEnter}
                                                    onMouseLeave={handlePopoverClose}
                                                    sx={{
                                                        backgroundColor: getCellColor(status, '0.8', 'lighten'),
                                                        fontSize: '0.8rem',
                                                        fontWeight: 'bold',
                                                        color: rosterData ? '#333' : theme.palette.text.secondary,
                                                        width: '100%',
                                                        height: '100%',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            transition: 'background-color 0.3s ease',
                                                            backgroundColor: getCellColor(status, '0.6', 'lighten'),
                                                            color: rosterData ? '#fff' : theme.palette.text.primary,
                                                        }
                                                    }}
                                                    onClick={() => handleCellClick(item, dateKey, rosterData)}
                                                >
                                                    {rosterData ? (statusCodes[status] || status) : '-'}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Popover
                        id="mouse-over-popover"
                        sx={{
                            pointerEvents: 'none',
                        }}
                        open={open}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'center',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'center',
                            horizontal: 'left',
                        }}
                        onClose={handlePopoverClose}
                        disableRestoreFocus
                    >
                        {popoverContent && <PopoverContent content={popoverContent} />}
                    </Popover>
                </TableContainer>
                <Toastr message={message} errors={errors} status={status} />
            </CalendarBox>
        </>
    );
};

// === Popover Content Component ===
const PopoverContent = ({ content }) => {
    console.log(content);
    return (
        <StyledPopoverBox status={content.details.status}>
            <Box className="popover-header">
                <Typography className="employee-name" variant="h6">
                    {content.employeeName}
                </Typography>
                <Typography className="date-display" variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {content.date}
                </Typography>
            </Box>
            <List className="popover-content">
                <ListItem>
                    <ListItemText 
                        primary="Status"
                        secondary={cleanName(content.details.status?.name || content.details.status)}
                    />
                </ListItem>
                {content.details.start_time && content.details.end_time && (
                    <ListItem>
                        <ListItemText 
                            primary="Time"
                            secondary={`${content.details.start_time} - ${content.details.end_time}`}
                        />
                    </ListItem>
                )}

                {/* Event Details */}
                {content.details.event_name && (
                    <ListItem>
                        <ListItemText 
                            primary="Event"
                            secondary={cleanName(content.details.event_name)}
                        />
                    </ListItem>
                )}

                {/* Venue Details */}
                {content.details.venue_name && (
                    <ListItem>
                        <ListItemText 
                            primary="Venue"
                            secondary={cleanName(content.details.venue_name) + ' - ' + cleanName(content.details?.location_name)}
                        />
                    </ListItem>
                )}

                {content.details.notes && (
                    <ListItem>
                        <ListItemText 
                            primary="Notes"
                            secondary={content.details.notes}
                        />
                    </ListItem>
                )}

                {content.details.is_locked === 1 && (
                    <ListItem>
                        <ListItemText 
                            primary="Status"
                            secondary="🔒 Locked Entry"
                        />
                    </ListItem>
                )}
            </List>
        </StyledPopoverBox>
    );
}
